import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '@/api'
import { MetadataForm } from '@/components/common/MetadataForm'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { parseLenientJson } from '@/lib/json'
import { useToast } from '@/hooks/useToast'
import { Plus, Search, Link2, CheckCircle2, ArrowLeft } from 'lucide-react'

export function MetadataIntegrationsPage() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo')

  const [searchQuery, setSearchQuery] = useState('')
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<string | null>(null)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [isEditOpen, setIsEditOpen] = useState(false)

  const { data: integrationList, isLoading } = useQuery({
    queryKey: ['metadata', 'integrations'],
    queryFn: () => api.metadata.integrations.list(),
  })

  const { data: integrationDetail } = useQuery({
    queryKey: ['metadata', 'integrations', selectedIntegrationId],
    queryFn: () => api.metadata.integrations.get(selectedIntegrationId!),
    enabled: !!selectedIntegrationId,
  })

  const createMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const validationRaw = typeof data.validation === 'string' ? data.validation.trim() : ''
      let validation: Record<string, unknown> = {}
      if (validationRaw) {
        try {
          validation = parseLenientJson(validationRaw)
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Invalid JSON'
          throw new Error(`Validation must be valid JSON: ${message}`)
        }
      }

      return api.metadata.integrations.create({
        integration_id: String(data.integration_id || ''),
        version: String(data.version || ''),
        integration_type: String(data.integration_type || ''),
        display_name: String(data.display_name || ''),
        provider: data.provider ? String(data.provider) : undefined,
        description: data.description ? String(data.description) : undefined,
        base_url: String(data.base_url || ''),
        status: data.status ? String(data.status) : undefined,
        is_demo: data.is_demo === true,
        validation,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['metadata', 'integrations'] })
      setIsCreateOpen(false)
      toast({
        title: 'Integration Created',
        description: 'Integration saved to MetadataIQ registry.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create integration',
        variant: 'destructive',
      })
    },
  })

  const validateMutation = useMutation({
    mutationFn: (integrationId: string) => api.metadata.integrations.validate(integrationId),
    onSuccess: (result) => {
      toast({
        title: result.validated ? 'Validation Succeeded' : 'Validation Failed',
        description: result.http_status ? `HTTP ${result.http_status}` : result.error || 'No response',
        variant: result.validated ? 'default' : 'destructive',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Validation failed',
        variant: 'destructive',
      })
    },
  })

  const updateMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      if (!integrationDetail) {
        throw new Error('No integration selected')
      }

      const validationRaw = typeof data.validation === 'string' ? data.validation.trim() : ''
      let validation: Record<string, unknown> | undefined
      if (validationRaw) {
        try {
          validation = parseLenientJson(validationRaw)
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Invalid JSON'
          throw new Error(`Validation must be valid JSON: ${message}`)
        }
      }

      return api.metadata.integrations.update(integrationDetail.integration_id, {
        version: integrationDetail.version,
        integration_type: data.integration_type ? String(data.integration_type) : undefined,
        display_name: data.display_name ? String(data.display_name) : undefined,
        provider: data.provider ? String(data.provider) : undefined,
        description: data.description ? String(data.description) : undefined,
        base_url: data.base_url ? String(data.base_url) : undefined,
        status: data.status ? String(data.status) : undefined,
        is_demo: data.is_demo === true,
        validation,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['metadata', 'integrations'] })
      queryClient.invalidateQueries({ queryKey: ['metadata', 'integrations', selectedIntegrationId] })
      setIsEditOpen(false)
      toast({
        title: 'Integration Updated',
        description: 'Integration definition updated.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update integration',
        variant: 'destructive',
      })
    },
  })

  const integrations = integrationList?.integrations ?? []
  const filteredIntegrations = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return integrations
    return integrations.filter((i) => {
      return (
        i.integration_id.toLowerCase().includes(q) ||
        i.display_name.toLowerCase().includes(q) ||
        i.integration_type.toLowerCase().includes(q) ||
        (i.provider || '').toLowerCase().includes(q)
      )
    })
  }, [integrations, searchQuery])

  return (
    <TooltipProvider delayDuration={300}>
      <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between gap-4">
        <div className="space-y-1">
          <h2 className="text-2xl font-display font-bold">Integrations</h2>
          <p className="text-muted-foreground">
            Shared integration registry for all modules (eMASS, scanners, repositories)
          </p>
        </div>
        <div className="flex items-center gap-2">
          {returnTo && (
            <Button asChild variant="outline">
              <Link to={returnTo}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Workflow
              </Link>
            </Button>
          )}
          <Button
            variant="glow"
            onClick={() => {
              setIsCreateOpen(true)
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            New Integration
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search integrations..."
              className="w-full h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <div className="space-y-2">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              filteredIntegrations.map((integration) => (
                <Tooltip key={`${integration.integration_id}-${integration.version}`}>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => setSelectedIntegrationId(integration.integration_id)}
                      className="w-full text-left"
                    >
                      <Card
                        className={`transition-all cursor-pointer ${
                          selectedIntegrationId === integration.integration_id
                            ? 'border-primary bg-primary/10'
                            : 'hover:border-muted-foreground/50'
                        }`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <p className="font-medium truncate">{integration.display_name}</p>
                              <p className="text-xs text-muted-foreground font-mono truncate">
                                {integration.integration_id} • v{integration.version}
                              </p>
                              <p className="text-xs text-muted-foreground truncate">
                                {integration.integration_type}
                                {integration.provider ? ` • ${integration.provider}` : ''}
                              </p>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              {integration.is_demo && <Badge variant="secondary">Demo</Badge>}
                              <Badge variant="outline">{integration.status}</Badge>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="right"
                    className="max-w-sm whitespace-pre-wrap"
                  >
                    {integration.description || 'No description provided.'}
                  </TooltipContent>
                </Tooltip>
              ))
            )}

            {filteredIntegrations.length === 0 && !isLoading && (
              <div className="text-center py-8 text-muted-foreground">
                <Link2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No integrations found</p>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-2">
          {selectedIntegrationId && integrationDetail ? (
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <CardTitle className="truncate">{integrationDetail.display_name}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      {integrationDetail.integration_type} • v{integrationDetail.version}
                      {integrationDetail.provider ? ` • ${integrationDetail.provider}` : ''}
                    </p>
                    <p className="text-xs text-muted-foreground font-mono mt-1">
                      {integrationDetail.integration_id}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{integrationDetail.status}</Badge>
                    {integrationDetail.is_demo && <Badge variant="secondary">Demo</Badge>}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setIsEditOpen(true)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => validateMutation.mutate(integrationDetail.integration_id)}
                      disabled={validateMutation.isPending}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Validate
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Base URL
                  </h4>
                  <p className="text-sm mt-2 font-mono break-all">{integrationDetail.base_url}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Description
                  </h4>
                  <p className="text-sm mt-2 text-muted-foreground">
                    {integrationDetail.description || 'No description provided.'}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Validation JSON
                  </h4>
                  <pre className="mt-2 rounded-lg border border-border bg-muted/30 p-4 text-xs overflow-auto">
                    {JSON.stringify(integrationDetail.validation, null, 2)}
                  </pre>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="h-full flex items-center justify-center min-h-[400px]">
              <div className="text-center text-muted-foreground">
                <Link2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Select an integration to view details</p>
              </div>
            </Card>
          )}
        </div>
      </div>

      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Integration</DialogTitle>
            <DialogDescription>
              Create a new shared integration for use across modules.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <div className="w-full">
              <MetadataForm
                schemaId="metadataiq-integration"
                initialData={{
                  version: '1.0.0',
                  integration_type: 'emass',
                  status: 'Active',
                  is_demo: false,
                  validation: '{\n  "path": "/",\n  "method": "GET",\n  "headers": {},\n  "params": {}\n}',
                }}
                onSubmit={async (data) => {
                  await createMutation.mutateAsync(data as Record<string, unknown>)
                }}
                submitLabel="Create"
                isSubmitting={createMutation.isPending}
                inline
              />
              <div className="flex justify-end mt-4">
                <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Integration</DialogTitle>
            <DialogDescription>
              Update the selected integration definition. Version updates modify the existing version.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <div className="w-full">
              <MetadataForm
                schemaId="metadataiq-integration"
                initialData={
                  integrationDetail
                    ? {
                        integration_id: integrationDetail.integration_id,
                        version: integrationDetail.version,
                        integration_type: integrationDetail.integration_type,
                        display_name: integrationDetail.display_name,
                        provider: integrationDetail.provider,
                        base_url: integrationDetail.base_url,
                        status: integrationDetail.status,
                        is_demo: integrationDetail.is_demo,
                        description: integrationDetail.description,
                        validation: JSON.stringify(integrationDetail.validation || {}, null, 2),
                      }
                    : undefined
                }
                onSubmit={async (data) => {
                  await updateMutation.mutateAsync(data as Record<string, unknown>)
                }}
                submitLabel="Save"
                isSubmitting={updateMutation.isPending}
                inline
              />
              <div className="flex justify-end mt-4">
                <Button variant="outline" onClick={() => setIsEditOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </TooltipProvider>
  )
}
